/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {FabAppLauncherItem} from '@fabric/components/common';
import {FabMessageService} from '@fabric/components/message';
import {VIEW_AUTHORITY} from './common/constants';
import {AuthenticationService} from './core/authentication/authentication.service';
import {EnvironmentService} from './core/environment/environment.service';
import {StateService} from './core/state/state.service';
import {User} from './models/user.model';
import {GfsUserService} from './services/gfs-user.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {

  public appName = 'Global Forecast System';
  public appVersion: string;
  public appVersionCodename: string;
  public appLauncherItems: FabAppLauncherItem[];
  public apiMap: unknown;
  public authorizedUser = false;
  public adminUser = false;
  public environmentName: string;
  public user: User;
  public userAuthorities: string[] = [];


  constructor(private readonly authenticationService: AuthenticationService,
              private readonly environmentService: EnvironmentService,
              private readonly router: Router,
              protected readonly stateService: StateService,
              private readonly gfsUserService: GfsUserService,
              public snackBar: FabMessageService) {

    // Enable Auth Silent Renew
    this.authenticationService.enableSilentRenew();

    const userInfo = this.authenticationService.userInfo;
    if (userInfo) {
      this.stateService.user = userInfo;
      this.gfsUserService.getUserByGmid(userInfo.gmid).subscribe((user: User) => {
          if (!user) {
            this.stateService.user.role = 'Not a user';
            this.authorizedUser = false;
            this.router.navigateByUrl('auth-error');
          } else {
            this.stateService.user.role = user.role;
            if (this.stateService.isSystemAdminRole()) {
              this.stateService.user.authorities = [
                VIEW_AUTHORITY.inActiveUserAuthority,
                VIEW_AUTHORITY.userAuthority,
                VIEW_AUTHORITY.regionalAdminAuthority,
                VIEW_AUTHORITY.centralOfficeAdminAuthority,
                VIEW_AUTHORITY.systemAdminAuthority
              ];
            } else if (this.stateService.isCentralOfficeAdminRole()) {
              this.stateService.user.authorities = [
                VIEW_AUTHORITY.inActiveUserAuthority,
                VIEW_AUTHORITY.userAuthority,
                VIEW_AUTHORITY.regionalAdminAuthority,
                VIEW_AUTHORITY.centralOfficeAdminAuthority
              ];
            } else if (this.stateService.isRegionalAdminRole()) {
              this.stateService.user.authorities = [
                VIEW_AUTHORITY.inActiveUserAuthority,
                VIEW_AUTHORITY.userAuthority,
                VIEW_AUTHORITY.regionalAdminAuthority
              ];
            } else if (this.stateService.isUserRole()) {
              this.stateService.user.authorities = [
                VIEW_AUTHORITY.inActiveUserAuthority,
                VIEW_AUTHORITY.userAuthority
              ];
            } else {
              this.stateService.user.authorities = [
                VIEW_AUTHORITY.inActiveUserAuthority
              ];
            }
            this.authorizedUser = true;
            this.adminUser = stateService.hasAuthority('admin-view');

            this.configAppInfo(this.environmentService.environment);
            this.configUserInfo();

            this.router.navigateByUrl('');
          }
        },
        (error) => {
          this.snackBar.displayError(error);
          this.router.navigateByUrl('auth-error');
        });
    }
  }

  async ngOnInit() {
    try {
      const userPreferences = await this.gfsUserService.getUserPreferences(this.stateService.user.gmid).toPromise();
      this.stateService.setUserPreferences(userPreferences);
    } catch (error) {
      this.snackBar.displayError(error);
    }
  }


  /**
   * Setup the app info configuration
   * @param env - the environment service variable to assign values to
   */
  configAppInfo(env): void {
    this.appName = env.appName;
    this.appVersion = env.appVersion;
    this.appVersionCodename = env.appVersionCodename;
    this.apiMap = env.apiMap;
    this.environmentName = env.environmentName;
  }

  /**
   * Setup user info with the logged-in user information
   */
  configUserInfo(): void {
    this.user = this.stateService.user;
    this.userAuthorities = this.user.authorities;
    this.gfsUserService.updateLastLogin(this.user.gmid).subscribe({
      error: (error) => {
        this.snackBar.displayError(error);
      }
    });
  }
}
